import { Injectable } from '@angular/core';
import { deepCopyArray, isStringEmpty, parseEmptyArray } from '@common/helpers';
import {
  LOAD_MASTER_SUBSCRIPTION,
  PassengerInformationInputModalEditArea,
  RegistrationLabelType,
  TravelerSupportBatteryType,
  TravelerSupportBatteryTypeUpdateTravelers,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MCountryI18n, MLangCodeConvert, MListData } from '@common/interfaces';
import { CountryCountryI18nListData } from '@common/interfaces/master/country_countryI18n_listData';
import { CountryI18nBy2LetterCode } from '@common/interfaces/master/country_i18n_by2lettercode';
import { CountryI18nPosCountry } from '@common/interfaces/master/country_i18n_pos_country';
import { MMileageProgramI18n } from '@common/interfaces/master/m_mileage_program_i18n';
import { CountryCodeNameType, PhoneNumberCountryType } from '@common/interfaces/mybooking/master-data.interface';
import { PassengerInformationInputMasterData } from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-Information-input-modal.interface';
import { PassengerType } from '@common/interfaces/mybooking/passenger-type';
import { LOAD_MASTERS } from '@common/master';
import { PassengerInformationInputModalStoreService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal-store.service';
import { MasterStoreKey } from '@conf/asw-master.config';
import { SupportClass } from '@lib/components/support-class/support-class';
import { matchedRegexPattern } from '@lib/helpers';
import { MOffice } from '@lib/interfaces';
import { DateFormatPipe } from '@lib/pipes';
import { StaticMsgPipe } from '@lib/pipes/static-msg/static-msg.pipe';
import { AswMasterService, SystemDateService } from '@lib/services';
import { CommonLibService } from '@lib/services/common-lib/common-lib.service';
import { BehaviorSubject, forkJoin, take } from 'rxjs';

/**
 * 搭乗者情報共通 service
 */
@Injectable()
export class PassengerInformationInputModalCommonService extends SupportClass {
  public isMasterDataLoaded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // ASWDB（マスタ）
  public masterData: PassengerInformationInputMasterData = {
    countries: [],
    i18nCountries: [],
    i18nMileagePrograms: [],
    listData: [],
    offices: [],
    posCountries: [],
    langCodeConverts: [],
    listDataAll: [],
  };
  // ソート順: POS管轄国.代表国フラグ(降順)
  public sortedPosCountries: Array<CountryI18nPosCountry> = [];
  // ソート順: 国(国際化).有償表示順(昇順)
  public sortedI18nCountries: Array<MCountryI18n> = [];
  // 検索条件: POS管轄国.POS国コード＝ユーザ共通情報.POS国コード(2レター)
  public countryCodeSetOfCurrentPosCountryCode: Set<string> = new Set();
  // 電話番号国リスト(管轄国・日本)
  public jpPhoneNumberCountryList: Array<PhoneNumberCountryType> = [];
  // 電話番号国リスト(管轄国・日本以外)
  public noJpPhoneNumberCountryList: Array<PhoneNumberCountryType> = [];
  // 国表示用のリスト(管轄国・日本)
  public jpCountryCodeNameList: Array<CountryCodeNameType> = [];
  // 国表示用のリスト(管轄国・日本以外)
  public noJpCountryCodeNameList: Array<CountryCodeNameType> = [];
  // 電話番号国リスト
  public phoneNumberCountryList: Array<PhoneNumberCountryType> = [];
  // 国表示用のリスト
  public countryCodeNameList: Array<CountryCodeNameType> = [];

  get lang(): string {
    return this._common.aswContextStoreService.aswContextData.lang;
  }

  get posCountryCode(): string {
    return this._common.aswContextStoreService.aswContextData.posCountryCode;
  }

  constructor(
    private _common: CommonLibService,
    private _staticMsg: StaticMsgPipe,
    private _dateFormatPipe: DateFormatPipe,
    private _passengerInfoInputModalStoreService: PassengerInformationInputModalStoreService,
    private _aswMasterService: AswMasterService,
    private _systemDateService: SystemDateService
  ) {
    super();
    this.loadMasterCache();
  }

  loadMasterCache() {
    this.addSubscription(
      LOAD_MASTER_SUBSCRIPTION,
      forkJoin([
        this._common.aswMasterService.load(
          [
            LOAD_MASTERS.COUNTRY_I18N_BY_2LETTERCODE,
            LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL,
            LOAD_MASTERS.M_MILEAGE_PROGRAM_I18N,
            LOAD_MASTERS.COUNTRY_COUNTRYI18N_LISTDATA,
            LOAD_MASTERS.M_OFFICE,
            LOAD_MASTERS.COUNTRY_I18N_POS_COUNTRY,
            LOAD_MASTERS.LISTDATA_ALL,
          ],
          true
        ),
        this._common.aswMasterService.getAswMasterByKey$(MasterStoreKey.LANGCODECONVERT_ALL).pipe(take(1)),
      ]).subscribe((master) => {
        const [
          [
            countryI18nBy2LetterCode,
            mCountryI18n,
            mMileageProgramI18n,
            mListData,
            mOffice,
            countryI18nPosCountry,
            mListDataAll,
          ],
          mLangCodeConvert,
        ] = master as [
          [
            CountryI18nBy2LetterCode[],
            MCountryI18n[],
            MMileageProgramI18n[],
            CountryCountryI18nListData[],
            MOffice[],
            CountryI18nPosCountry[],
            MListData[]
          ],
          MLangCodeConvert[]
        ];

        this.masterData = {
          countries: parseEmptyArray(Object.values(countryI18nBy2LetterCode).map((value: any) => value?.[0])),
          i18nCountries: parseEmptyArray(mCountryI18n),
          i18nMileagePrograms: parseEmptyArray(mMileageProgramI18n),
          listData: parseEmptyArray(mListData),
          offices: parseEmptyArray(mOffice),
          posCountries: parseEmptyArray(countryI18nPosCountry),
          langCodeConverts: parseEmptyArray(mLangCodeConvert),
          listDataAll: parseEmptyArray(mListDataAll),
        };

        this.handleListCreationProcess();

        this.isMasterDataLoaded$.next(true);
      })
    );
  }

  /**
   * (1.11) 以下処理にて、リストの作成処理を行う。
   */
  handleListCreationProcess() {
    this.countryCodeSetOfCurrentPosCountryCode = new Set(
      this.masterData.posCountries
        ?.filter((posCountry) => posCountry.pos_country_code === this.posCountryCode)
        ?.map((item) => item.country_2letter_code)
    );

    this.prepareMasterList();
    this.createPhoneCountryInfoList();
    this.filterMileageProgramMaster();
    this.createIssuingCountryCodeNameList();
  }

  /**
   * (1.11.1) 以下の処理にて、電話番号国リストを作成する。
   */
  createPhoneCountryInfoList(): void {
    this.createJpPhoneCountryInfoList();
    this.createNoJpPhoneCountryInfoList();
    this.handleUsCanadaPhoneCountryInfoList();

    this.phoneNumberCountryList.push({
      country2LetterCode: '',
      country3LetterCode: '',
      countryName: this._staticMsg.transform('placeholder.pleaseSelect'),
    });

    this.jpPhoneNumberCountryList?.forEach((item) => {
      this.phoneNumberCountryList.push({
        country2LetterCode: item.country2LetterCode,
        country3LetterCode: item.country3LetterCode,
        countryName: item.countryName,
      });
    });

    this.phoneNumberCountryList.push({
      country2LetterCode: '',
      country3LetterCode: '',
      countryName: this._staticMsg.transform('pulldown.separatorHyphen'),
    });

    this.noJpPhoneNumberCountryList?.forEach((item) => {
      this.phoneNumberCountryList.push({
        country2LetterCode: item.country2LetterCode,
        country3LetterCode: item.country3LetterCode,
        countryName: item.countryName,
      });
    });
  }

  prepareMasterList() {
    this.sortedPosCountries = [...this.masterData.posCountries].sort(
      (a, b) => Number(b.revenue_display_order) - Number(a.revenue_display_order)
    );

    this.sortedI18nCountries = [...this.masterData.i18nCountries].sort(
      (a, b) => a.revenue_display_order - b.revenue_display_order
    );
  }

  createJpPhoneCountryInfoList(): void {
    const _jpPhoneNumberCountryList: Array<PhoneNumberCountryType> = [];
    const countryMap = new Map<string, CountryI18nBy2LetterCode>();

    this.masterData.countries?.forEach((country) => {
      // 検索条件: 国.連絡先電話番号国フラグ＝true
      if (country?.contact_tel_number_country_flg) {
        countryMap.set(country.country_2letter_code, country);
      }
    });

    // (1.11.1.1) 以下の条件でASWDB（マスタ）のPOS管轄国を取得する。
    this.sortedPosCountries?.forEach((posCountry) => {
      // 検索条件: POS管轄国.POS国コード＝ユーザ共通情報.POS国コード(2レター)
      if (posCountry.pos_country_code === this.posCountryCode) {
        // 検索条件: 国.国コード(2 レター)＝POS管轄国.国コード(2 レター)
        const country = countryMap.get(posCountry.country_2letter_code);

        if (country) {
          // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
          const countryI18n = this.masterData.i18nCountries.find(
            (item) => item.country_3letter_code === country.country_3letter_code
          );

          if (countryI18n) {
            _jpPhoneNumberCountryList.push({
              country2LetterCode: country.country_2letter_code,
              country3LetterCode: country.country_3letter_code,
              countryName: countryI18n.country_name ?? '',
            });
          }
        }
      }
    });

    let isIncludeJp: boolean = false;
    // (1.11.1.2) POS管轄国のリストを繰り返し、POS管轄国.国コード(2レター)=“JP”(日本)となるPOS管轄国が存在しない場合
    _jpPhoneNumberCountryList.forEach((item) => {
      this.jpPhoneNumberCountryList.push(item);

      if (!isIncludeJp) {
        isIncludeJp = item.country2LetterCode === 'JP';
      }
    });

    // 以下の内容で日本の国情報を取得し、POS管轄国のリストに追加する。
    if (!isIncludeJp) {
      // 検索条件: 国.国コード(2 レター)＝“JP”
      const jpCountry = countryMap.get('JP');

      if (jpCountry) {
        // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
        const jpCountryI18n = this.masterData.i18nCountries.find(
          (item) => item.country_3letter_code === jpCountry.country_3letter_code
        );

        if (jpCountryI18n) {
          this.jpPhoneNumberCountryList.push({
            country2LetterCode: jpCountry.country_2letter_code,
            country3LetterCode: jpCountry.country_3letter_code,
            countryName: jpCountryI18n.country_name ?? '',
          });
        }
      }
    }
  }

  createNoJpPhoneCountryInfoList(): void {
    const countryMap = new Map<string, CountryI18nBy2LetterCode>();

    const sortedMasterDataCountries = this.sortedI18nCountries
      .map((ref) => this.masterData.countries.find((item) => item.country_2letter_code === ref.country_2letter_code))
      .filter((item): item is CountryI18nBy2LetterCode => item !== undefined);

    sortedMasterDataCountries?.forEach((country) => {
      if (
        // 検索条件: 国.連絡先電話番号国フラグ＝true
        country.contact_tel_number_country_flg &&
        // 検索条件: 国.国コード(2 レター)≠“JP”
        country.country_2letter_code !== 'JP' &&
        // 検索条件: 搭乗者SMS送信可否=true
        country.sms_send_flag
      ) {
        countryMap.set(country.country_2letter_code, country);
      }
    });

    // (1.11.1.3) 以下の処理にて、電話番号国リスト(管轄国・日本以外)を作成する。
    countryMap.forEach((country) => {
      // 検索条件: 国.国コード(2 レター)が、POS管轄国.国コード=ユーザ共通情報.POS国コード(2レター)となるPOS管轄国.国コード(2レター)に含まれない
      if (!this.countryCodeSetOfCurrentPosCountryCode.has(country.country_2letter_code)) {
        // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
        const countryI18n = this.masterData.i18nCountries.find(
          (item) => item.country_3letter_code === country.country_3letter_code
        );

        if (countryI18n) {
          this.noJpPhoneNumberCountryList.push({
            country2LetterCode: country.country_2letter_code,
            country3LetterCode: country.country_3letter_code,
            countryName: countryI18n.country_name ?? '',
          });
        }
      }
    });
  }

  /**
   * (1.11.1.4) ASWDB からリスト取得後、以下の処理にて電話番号国名称を上書きする。
   */
  handleUsCanadaPhoneCountryInfoList(): void {
    const pd019Map = new Map<string, string>();

    // (1.11.1.4.1) 汎用マスターデータ(リスト)を取得する。
    this.masterData.listData?.forEach((data) => {
      if (
        // 検索条件: データコード＝“PD_019”
        data?.data_code === 'PD_019'
      ) {
        pd019Map.set(data.country_3letter_code, data.country_name);
      }
    });

    this.jpPhoneNumberCountryList?.forEach((jpPhoneNumberCountry) => {
      // 検索条件: 汎用マスターデータ(リスト).value ＝ 国コード(3レター)
      const displayContent = pd019Map.get(jpPhoneNumberCountry.country3LetterCode);

      if (displayContent) {
        jpPhoneNumberCountry.countryName = displayContent;
      }
    });

    this.noJpPhoneNumberCountryList?.forEach((noJpPhoneNumberCountry) => {
      // 検索条件: 汎用マスターデータ(リスト).value ＝ 国コード(3レター)
      const displayContent = pd019Map.get(noJpPhoneNumberCountry.country3LetterCode);

      if (displayContent) {
        noJpPhoneNumberCountry.countryName = displayContent;
      }
    });
  }

  /**
   * (1.11.2) 以下の処理にて、ASWDB(マスタ)よりマイレージプログラムを取得し、マイレージプログラム情報として保持する。
   */
  filterMileageProgramMaster(): void {
    const currentDate = new Date();

    // 検索条件: 適用開始日≦現在日付 AND 適用終了日≧ 現在日付
    this.masterData.i18nMileagePrograms.filter((i18nMileage) => {
      const startDate = new Date(i18nMileage.apply_from_date);
      const endDate = new Date(i18nMileage.apply_to_date);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      return startDate <= currentDate && endDate >= currentDate;
    });
  }

  createIssuingCountryCodeNameList(): void {
    this.createJpIssuingCountryCodeNameList();
    this.createNoJpIssuingCountryCodeNameList();
    this.handleHkUkIssuingCountryCodeNameList();

    const placeholderEntry: CountryCodeNameType = {
      country2Code: '',
      country3Code: '',
      countryName: this._staticMsg.transform('placeholder.pleaseSelect'),
      isTranslate: false,
    };

    const separatorEntry: CountryCodeNameType = {
      country2Code: '',
      country3Code: '',
      countryName: this._staticMsg.transform('pulldown.separatorHyphen'),
      isTranslate: false,
    };

    this.countryCodeNameList.push(placeholderEntry);

    this.jpCountryCodeNameList.forEach((item) => {
      this.countryCodeNameList.push({ ...item });
    });

    this.countryCodeNameList.push(separatorEntry);

    this.noJpCountryCodeNameList.forEach((item) => {
      this.countryCodeNameList.push({ ...item });
    });

    // add country UNO for issuanceCountryCode and nationalityCode
    const unoCountry: CountryCodeNameType = {
      country2Code: 'UN',
      country3Code: 'UNO',
      countryName: 'UNO',
      isTranslate: false,
    };
    this.countryCodeNameList.push(unoCountry);
  }

  createJpIssuingCountryCodeNameList(): void {
    const _jpCountryCodeNameList: Array<CountryCodeNameType> = [];
    const countryMap = new Map<string, CountryI18nBy2LetterCode>();

    this.masterData.countries?.forEach((country) => {
      // 検索条件: 国.APIS/パスポートフラグ
      if (country?.apis_passport_flag) {
        countryMap.set(country.country_2letter_code, country);
      }
    });

    // (1.11.3.1) OS管轄国.国コード(2レター)＝”JP”(日本)の場合、ASWDBから以下の内容でPOS管轄国を取得し、POS管轄国リスト(管轄国・日本)する。
    this.sortedPosCountries?.forEach((posCountry) => {
      // 検索条件: POS管轄国.POS国コード＝ユーザ共通情報.POS国コード(2レター)
      if (posCountry.pos_country_code === this.posCountryCode) {
        // 検索条件: 国.国コード(2 レター)＝POS管轄国.国コード(2 レター)
        const country = countryMap.get(posCountry.country_2letter_code);

        if (country) {
          // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
          const countryI18n = this.masterData.i18nCountries.find(
            (item) => item.country_3letter_code === country.country_3letter_code
          );

          if (countryI18n) {
            _jpCountryCodeNameList.push({
              country2Code: country.country_2letter_code,
              country3Code: country.country_3letter_code,
              countryName: countryI18n.country_name ?? '',
              isTranslate: !countryI18n.country_name,
            });
          }
        }
      }
    });

    let isIncludeJp: boolean = false;
    // (1.11.3.2) POS管轄国リスト(管轄国・日本)から、POS管轄国.国コード(2レター)≠ “JP”(日本)を満たす場合、以下の処理を行う。
    _jpCountryCodeNameList?.forEach((item, index) => {
      this.jpCountryCodeNameList.push({
        ...item,
      });
      if (!isIncludeJp) {
        isIncludeJp = item.country2Code === 'JP';
      }
    });

    // (1.11.3.2.1) ASWDBから以下の内容で日本の国情報を取得し、日本国情報とする。
    if (!isIncludeJp) {
      // 検索条件: 国.国コード(2 レター)＝“JP”
      const jpCountry = countryMap.get('JP');

      if (jpCountry) {
        // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
        const jpCountryI18n = this.masterData.i18nCountries.find(
          (item) => item.country_3letter_code === jpCountry.country_3letter_code
        );

        if (jpCountryI18n) {
          this.jpCountryCodeNameList.push({
            country2Code: jpCountry.country_2letter_code,
            country3Code: jpCountry.country_3letter_code,
            countryName: jpCountryI18n.country_name ?? '',
            isTranslate: !jpCountryI18n.country_name,
          });
        }
      }
    }
  }

  createNoJpIssuingCountryCodeNameList(): void {
    const countryMap = new Map<string, CountryI18nBy2LetterCode>();

    this.masterData.countries?.forEach((country) => {
      if (
        // 検索条件: 国.APIS/パスポートフラグ ＝ true
        country.apis_passport_flag &&
        // 検索条件: 国.国コード(2 レター)≠“JP”
        country.country_2letter_code !== 'JP'
      ) {
        countryMap.set(country.country_2letter_code, country);
      }
    });

    // (1.11.1.3) 以下の処理にて、電話番号国リスト(管轄国・日本以外)を作成する。
    countryMap.forEach((country) => {
      // 検索条件: 国.国コード(2 レター)が、POS管轄国.国コード=ユーザ共通情報.POS国コード(2レター)となるPOS管轄国.国コード(2レター)に含まれない
      if (!this.countryCodeSetOfCurrentPosCountryCode.has(country.country_2letter_code)) {
        // 検索条件: 国(国際化).国コード(3 レター)＝国.国コード(3 レター)
        const countryI18n = this.masterData.i18nCountries.find(
          (item) => item.country_3letter_code === country.country_3letter_code
        );

        if (countryI18n) {
          this.noJpCountryCodeNameList.push({
            country2Code: country.country_2letter_code,
            country3Code: country.country_3letter_code,
            countryName: countryI18n.country_name ?? '',
            isTranslate: !countryI18n.country_name,
          });
        }
      }
    });
  }

  handleHkUkIssuingCountryCodeNameList(): void {
    const pd018Map = new Map<string, string>();

    // (1.11.1.4.1) 汎用マスターデータ(リスト)を取得する。
    this.masterData.listData?.forEach((data) => {
      if (
        // 検索条件: データコード＝“PD_018”
        data.data_code === 'PD_018'
      ) {
        pd018Map.set(data.country_3letter_code, data.country_name);
      }
    });

    this.jpCountryCodeNameList?.forEach((jpCountryCodeName, index) => {
      // 検索条件: 汎用マスターデータ(リスト).value ＝ 国コード(3レター)
      const displayContent = pd018Map.get(jpCountryCodeName.country3Code);

      if (displayContent) {
        jpCountryCodeName.countryName = displayContent;
      }
    });

    this.noJpCountryCodeNameList?.forEach((noJpCountryCodeName, index) => {
      // 検索条件: 汎用マスターデータ(リスト).value ＝ 国コード(3レター)
      const displayContent = pd018Map.get(noJpCountryCodeName.country3Code);

      if (displayContent) {
        noJpCountryCodeName.countryName = displayContent;
      }
    });
  }

  /**
   * 入力モーダルが特定のエリアおよび乗客に対して編集モードであるかどうかを確認します。
   * @param area 編集モードをチェックする領域。 @see PassengerInformationInputModalEditArea
   * @param passengerIndex
   * @returns 入力モーダルが指定されたエリアと乗客に対して編集モードであるかどうかを示すブール値。
   */
  isEditMode(area: PassengerInformationInputModalEditArea, travelerId: string = ''): boolean {
    let result: boolean = this._passengerInfoInputModalStoreService.editArea === area;

    if (!isStringEmpty(travelerId)) {
      return result && this._passengerInfoInputModalStoreService.travelerId === travelerId;
    }

    return result;
  }

  /**
   * 登録状況の取得
   * @param isEditMode 編集中か否か（前画面情報）
   * @param isRegistered 代表者情報登録済みか否か
   * @returns 登録状況ラベル
   */
  getRegistrationLabel(isEditMode: boolean, isRegistered: boolean) {
    if (isEditMode) {
      return RegistrationLabelType.EDITING;
    }

    if (isRegistered) {
      return RegistrationLabelType.REGISTERED;
    }

    return RegistrationLabelType.NOT_REGISTERED;
  }

  /**
   * 登録状況クラスを取得する
   * @param registration  登録状況ラベル
   * @returns 登録状況クラス
   */
  getRegistrationLabelClass(registration: string) {
    const labelClassMap = {
      [RegistrationLabelType.REGISTERED]: 'p-booking-info-mdl__open-state--registered',
      [RegistrationLabelType.NOT_REGISTERED]: 'p-booking-info-mdl__open-state--not-registered',
      [RegistrationLabelType.EDITING]: 'p-booking-info-mdl__open-state--editing',
    };

    return labelClassMap[registration] || 'p-booking-info-mdl__open-state--not-registered';
  }

  /**
   * 車いすバッテリータイプ変換処理
   * @param batteryType バッテリータイプ
   * @return  キャッシュバッテリータイプ種別
   */
  getWheelchairBatteryDisplayValue(batteryType: string) {
    const wheelchairBatteryMap = {
      [TravelerSupportBatteryType.NICKEL_CADMIUM]: '1', // ニッカド
      [TravelerSupportBatteryType.NICKEL_METAL_HYDRIDE]: '2', // ニッケル水素
      [TravelerSupportBatteryType.LITHIUM_ION]: '3', // リチウムイオン
      [TravelerSupportBatteryType.SHIELD_BATTERY]: '6', // シールド(カート取得APIから返却される形)
      [TravelerSupportBatteryType.NON_SHIELD_BATTERY]: '7', // ノンシールド(カート取得APIから返却される形)
    };

    return wheelchairBatteryMap[batteryType] || batteryType;
  }

  createBatteryRequest(batteryType: string) {
    switch (batteryType) {
      case '1':
        return TravelerSupportBatteryTypeUpdateTravelers.NICKEL_CADMIUM;
      case '2':
        return TravelerSupportBatteryTypeUpdateTravelers.NICKEL_METAL_HYDRIDE;
      case '3':
        return TravelerSupportBatteryTypeUpdateTravelers.LITHIUM_ION;
      case '6':
        return TravelerSupportBatteryTypeUpdateTravelers.SHIELD_BATTERY;
      case '7':
        return TravelerSupportBatteryTypeUpdateTravelers.NON_SHIELD_BATTERY;
    }
    return undefined;
  }

  /**
   * 車いす持ち込みラベルの取得
   * @param isNeededWheelchair 車いす有無
   * @returns 車いす持ち込みラベル
   */
  getBringWheelchairDisplayValue(isNeededWheelchair: boolean) {
    return isNeededWheelchair ? 'label.willBeCheckedIn' : 'label.willNotBeCheckedIn';
  }

  /**
   * 車いす折り畳みラベルの取得
   * @param isWheelchairFoldable 車いす折りたたみ
   * @returns 車いす折り畳みラベル
   */
  getWheelchairFoldableDisplayValue(isWheelchairFoldable: boolean) {
    return isWheelchairFoldable ? 'label.foldable' : 'label.notFoldable';
  }

  /**
   * 車いす種別ラベルの取得
   * @param wheelchairType 車いす種別
   * @returns 車いす種別ラベル
   */
  getWheelchairTypeDisplayValue(wheelchairType: string) {
    if (wheelchairType === 'manual') {
      return 'label.manual';
    }

    return wheelchairType === 'battery' ? 'label.electric' : '';
  }

  /**
   * 生の日付文字列または Date オブジェクトを Date オブジェクトに変換します。
   * @param rawDate 生の日付文字列または Date オブジェクト。
   * @returns 変換された Date オブジェクト、または入力が null または未定義の場合は null。
   */
  getDate(rawDate?: string | Date | null): Date | null {
    return rawDate && rawDate !== null ? new Date(rawDate) : null;
  }

  getYmdString(date?: string) {
    if (!date) {
      return '';
    }

    return this._dateFormatPipe.transform(date, 'default_apiDate');
  }

  /**
   * 生年月日と基準日から年齢を計算します
   * @param birthDate 生年月日
   * @param baseDate 年齢を計算する基準日
   * @returns 乗客の年齢
   */
  getAge(birthDate: Date, baseDate: Date): number {
    if (!birthDate || !baseDate) {
      return 0;
    }

    if (isNaN(birthDate.getTime())) {
      birthDate = this._systemDateService.getSystemDate();
    }

    if (isNaN(baseDate.getTime())) {
      baseDate = this._systemDateService.getSystemDate();
    }

    let diffMYear = baseDate.getFullYear() - birthDate.getFullYear();
    const diffMonth = baseDate.getMonth() - birthDate.getMonth();

    if (diffMonth < 0 || (diffMonth === 0 && baseDate.getDate() < birthDate.getDate())) {
      diffMYear--;
    }

    return diffMYear;
  }

  /**
   * 搭乗者種別のラベルを取得 搭乗者入力などで使用　文言がオリジナルのため作成
   * @param code 搭乗者種別コード
   * @return 搭乗者種別のラベル
   */
  getPassengerLabel(code: string) {
    const passengerTypeMap: Record<string, string> = {
      [PassengerType.ADT]: 'label.adult',
      [PassengerType.B15]: 'label.youngAdult',
      [PassengerType.CHD]: 'label.child',
      [PassengerType.INS]: 'label.ins',
      [PassengerType.INF]: 'label.infant',
    };

    return passengerTypeMap[code] || 'label.adult';
  }

  // ミュンヘン国際空港の現地時間(＝UTC+2)を取得して
  getExpiryDateMin(): Date {
    return this._systemDateService.getAirportLocalDateByAirportCode('MUC');
  }

  // CATEGORY ="servicing" AND KEY ="visaGreenCardPassport.expirationDate.max"
  getExpiryDateMax(): Date {
    const dateMaxKey = this._aswMasterService.getMPropertyByKey(
      'servicing',
      'visaGreenCardPassport.expirationDate.max'
    );
    const expiryDateMax = this.getExpiryDateMin();
    expiryDateMax.setFullYear(expiryDateMax.getFullYear() + parseInt(dateMaxKey, 10));
    return expiryDateMax;
  }

  checkDateInRange(dateFrom: Date, dateTo: Date, dateCheck: Date): boolean {
    const _compareDate1 = new Date(dateCheck);
    _compareDate1.setHours(23, 59, 59, 999);
    const _compareDate2 = new Date(dateCheck);
    _compareDate2.setHours(0, 0, 0, 0);

    return _compareDate1 < dateFrom || dateTo < _compareDate2;
  }

  /**
   * 電話番号に対してSMSを送信できるかを判定する
   * @param phoneNumber 電話番号
   * @returns 電話番号に対してSMSを送信できるかどうか
   */
  isSmsSendPattern(phoneNumber: string) {
    const smsSendPattern = '^0[789]0|^[789]0';

    return matchedRegexPattern(phoneNumber, smsSendPattern);
  }

  copy<T extends {}>(obj: T): T {
    const newObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== null && value !== undefined && typeof value === 'object') {
        if (Array.isArray(value)) {
          newObj[key as keyof T] = deepCopyArray(value);
        } else if (value instanceof Date) {
          newObj[key as keyof T] = value;
        } else {
          newObj[key as keyof T] = { ...this.copy(value) };
        }
      } else {
        newObj[key as keyof T] = value;
      }
    }
    return newObj as T;
  }

  equal<T>(a: T, b: T): boolean {
    if (a instanceof Date && b instanceof Date) {
      return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
    }

    if (a === b) {
      return true;
    }

    if (typeof a !== 'object' || typeof b !== 'object' || a == null || b == null) {
      return false;
    }

    const keysA = Object.keys(a) as Array<keyof T>;
    const keysB = Object.keys(b) as Array<keyof T>;

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (!keysB.includes(key) || !this.equal(a[key], b[key])) {
        return false;
      }
    }

    return true;
  }

  /** @inheritdoc */
  override destroy(): void {
    this.deleteSubscription(LOAD_MASTER_SUBSCRIPTION);
  }
}
